/**
 * Move 1 — frontend ingestion service.
 *
 * Exports the open Excel workbook as XLSX bytes via Office.js, uploads to
 * the backend's /api/workbook/ingest endpoint, and returns the session
 * summary the agent's query_workbook tool will key off.
 *
 * Office.js exposes raw XLSX bytes via getFileAsync(FileType.Compressed),
 * which streams a slice-based protocol: getSliceAsync() retrieves chunks
 * up to ~4 MB each. We reassemble into one Uint8Array, then upload as a
 * multipart form to the backend.
 */

import { IngestSummary } from '../types/ingest';
import { getBootstrapToken } from './sso';

const API_BASE_URL = process.env.API_BASE_URL || 'http://localhost:3001';

// Office.js is loaded globally by the Office runtime — types come from
// the office-js global namespace declarations in @types/office-js.

/**
 * Pull the open workbook out of Office.js as XLSX bytes.
 *
 * The Office.js API requires us to (a) start a "file handle" via
 * getFileAsync, (b) iterate getSliceAsync() to pull chunks, (c) close the
 * handle. The whole dance happens inside one Promise.
 */
export async function exportWorkbookAsXlsx(): Promise<Uint8Array> {
  return new Promise<Uint8Array>((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: 4 * 1024 * 1024 },
      (result: any) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(result.error?.message ?? 'getFileAsync failed'));
          return;
        }
        const file = result.value;
        const sliceCount = file.sliceCount;
        const slices: Uint8Array[] = new Array(sliceCount);
        let received = 0;

        if (sliceCount === 0) {
          file.closeAsync(() => resolve(new Uint8Array(0)));
          return;
        }

        for (let i = 0; i < sliceCount; i++) {
          file.getSliceAsync(i, (sliceResult: any) => {
            if (sliceResult.status !== Office.AsyncResultStatus.Succeeded) {
              file.closeAsync(() => {});
              reject(new Error(sliceResult.error?.message ?? `getSliceAsync(${i}) failed`));
              return;
            }
            const data = sliceResult.value.data;
            // data may be a Uint8Array, Array of bytes, or base64 string depending on host
            let bytes: Uint8Array;
            if (data instanceof Uint8Array) {
              bytes = data;
            } else if (Array.isArray(data)) {
              bytes = new Uint8Array(data);
            } else if (typeof data === 'string') {
              // Treat as base64
              const bin = atob(data);
              bytes = new Uint8Array(bin.length);
              for (let j = 0; j < bin.length; j++) bytes[j] = bin.charCodeAt(j);
            } else {
              file.closeAsync(() => {});
              reject(new Error(`Unsupported slice data type: ${typeof data}`));
              return;
            }
            slices[sliceResult.value.index ?? i] = bytes;
            received++;
            if (received === sliceCount) {
              file.closeAsync(() => {
                const total = slices.reduce((n, s) => n + s.length, 0);
                const out = new Uint8Array(total);
                let off = 0;
                for (const s of slices) {
                  out.set(s, off);
                  off += s.length;
                }
                resolve(out);
              });
            }
          });
        }
      },
    );
  });
}

export interface IngestResult extends IngestSummary {
  xhr: { status: number };
}

/**
 * Export the open workbook + upload to backend. Returns the session
 * summary which the caller stores against the active conversation.
 */
export async function ingestCurrentWorkbook(opts?: {
  fileName?: string;
  /** Override token for tests. Production code should leave this unset; the
   *  function calls getBootstrapToken() itself. */
  authToken?: string;
}): Promise<IngestResult> {
  const bytes = await exportWorkbookAsXlsx();
  if (bytes.length === 0) {
    throw new Error('Workbook is empty — nothing to ingest.');
  }

  const fileName = opts?.fileName ?? inferFileName();

  // Build a Blob and FormData for multipart upload.
  // exceljs accepts the standard Office Open XML MIME for xlsx.
  const blob = new Blob([bytes as BlobPart], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const form = new FormData();
  form.append('file', blob, fileName);

  // The /api/workbook/ingest route became auth-gated 2026-05-17 (security
  // audit P0-1). Fetch a real bootstrap token here so the caller doesn't
  // have to remember to pass one. Tests can still override via opts.authToken.
  const token = opts?.authToken ?? (await getBootstrapToken());
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };

  const resp = await fetch(`${API_BASE_URL}/api/workbook/ingest`, {
    method: 'POST',
    body: form,
    headers,
    credentials: 'include',  // Move 5 follow-up Step 14 — first call sets the AWSALB cookie; later calls send it
  });

  if (!resp.ok) {
    let errorMsg = `Ingest failed (HTTP ${resp.status})`;
    try {
      const body = await resp.json();
      if (body?.error) errorMsg = body.error;
    } catch { /* keep status-only message */ }
    throw new Error(errorMsg);
  }

  const summary = (await resp.json()) as IngestSummary;
  return { ...summary, xhr: { status: resp.status } };
}

function inferFileName(): string {
  try {
    // Office.js exposes the workbook URL/path in some hosts; fall back to a default.
    const url = (Office.context.document as { url?: string }).url;
    if (url) {
      const last = url.split(/[/\\]/).pop();
      if (last) return last;
    }
  } catch { /* ignore */ }
  return 'workbook.xlsx';
}
